import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Video,
  Image,
  Music,
  Sparkles,
  Crown,
  MessageSquare,
  Coins,
  ArrowRight,
  Zap,
  Stamp,
  Copy,
  Disc3,
  Wand2,
  Layers,
  Youtube,
  Mic,
  Users
} from "lucide-react";


interface ServiceCard {
  id: string;
  titleTh: string;
  titleEn: string;
  descriptionTh: string;
  descriptionEn: string;
  icon: React.ReactNode;
  credits: string;
  link: string;
  color: string;
  popular?: boolean;
  badge?: { th: string; en: string };
}

const services: ServiceCard[] = [
  {
    id: "veo31",
    titleTh: "Veo 3.1 Fast",
    titleEn: "Veo 3.1 Fast",
    descriptionTh: "สร้างวิดีโอ 8 วินาที ด้วย AI รุ่นใหม่ล่าสุด รองรับ Text และ Image to Video",
    descriptionEn: "Generate 8s videos with the latest AI model. Supports Text and Image to Video.",
    icon: <Video className="h-8 w-8" />,
    credits: "60",
    link: "/generate/video",
    color: "from-cyan-500 to-blue-500",
    popular: true,
    badge: { th: "แนะนำ", en: "Recommended" }
  },
  {
    id: "seedance2",
    titleTh: "Seedance 2.0",
    titleEn: "Seedance 2.0",
    descriptionTh: "Seedance 2.0 Fast สร้างวิดีโอคุณภาพสูง เลือกความยาว 10 หรือ 15 วินาที",
    descriptionEn: "Seedance 2.0 Fast creates high-quality videos. Choose 10s or 15s duration.",
    icon: <Sparkles className="h-8 w-8" />,
    credits: "330-495",
    link: "/generate/sora/text-to-video",
    color: "from-purple-500 to-pink-500",
    popular: true
  },
  {
    id: "grok",
    titleTh: "Grok Imagine",
    titleEn: "Grok Imagine",
    descriptionTh: "สร้างรูปภาพและวิดีโอด้วย Grok AI จาก xAI รองรับ Text/Image to Video และ Image",
    descriptionEn: "Generate images and videos with Grok AI from xAI. Supports Text/Image to Video and Image.",
    icon: <Sparkles className="h-8 w-8" />,
    credits: "5-35",
    link: "/generate/grok",
    color: "from-blue-500 to-cyan-500",
    badge: { th: "ใหม่", en: "New" }
  },
  {
    id: "music",
    titleTh: "สร้างเพลง AI",
    titleEn: "AI Music Generation",
    descriptionTh: "สร้างเพลงด้วย Suno V6 รองรับทั้งเพลงมีเนื้อร้องและ Instrumental",
    descriptionEn: "Generate music with Suno V6. Supports both vocal and instrumental tracks.",
    icon: <Music className="h-8 w-8" />,
    credits: "12",
    link: "/generate/music",
    color: "from-green-500 to-emerald-500"
  },
  {
    id: "dance",
    titleTh: "สร้างวิดีโอเต้น",
    titleEn: "AI Dance Video",
    descriptionTh: "สร้างวิดีโอเต้นจากรูปภาพ ใส่ท่าเต้นให้ตัวละครของคุณเคลื่อนไหวตามจังหวะ",
    descriptionEn: "Create dance videos from images. Add dance moves to make your character move to the beat.",
    icon: <Disc3 className="h-8 w-8" />,
    credits: "80",
    link: "/generate/motion",
    color: "from-fuchsia-500 to-purple-500",
    badge: { th: "ใหม่", en: "New" }
  },
  {
    id: "transform",
    titleTh: "สร้างคลิป สร้างบ้าน Time lapse",
    titleEn: "Build House Time Lapse",
    descriptionTh: "สร้างวิดีโอเปลี่ยนฉากจากรูปเริ่มต้นไปยังรูปสิ้นสุด ด้วย Veo 3.1",
    descriptionEn: "Generate transition videos from start to end images using Veo 3.1",
    icon: <Wand2 className="h-8 w-8" />,
    credits: "60",
    link: "/generate/transform",
    color: "from-cyan-500 to-blue-500",
    badge: { th: "ใหม่", en: "New" }
  },
  {
    id: "photo",
    titleTh: "สร้างรูปภาพ AI",
    titleEn: "AI Image Generation",
    descriptionTh: "สร้างรูปภาพด้วย GPT Image, Seedream 4.5, Nano Banana",
    descriptionEn: "Generate images with GPT Image, Seedream 4.5, Nano Banana.",
    icon: <Image className="h-8 w-8" />,
    credits: "4",
    link: "/generate/photo",
    color: "from-rose-500 to-red-500"
  },
  {
    id: "cover",
    titleTh: "สร้างภาพปก",
    titleEn: "Cover Generator",
    descriptionTh: "สร้างภาพปก YouTube / Facebook ด้วย AI พร้อมเทมเพลตสำเร็จรูป",
    descriptionEn: "Generate YouTube / Facebook covers with AI and ready-made templates.",
    icon: <Youtube className="h-8 w-8" />,
    credits: "4",
    link: "/generate/cover",
    color: "from-red-500 to-pink-500",
    badge: { th: "ใหม่", en: "New" }
  },
  {
    id: "clone-img",
    titleTh: "Clone IMG",
    titleEn: "Clone IMG",
    descriptionTh: "สร้าง Prompt จากภาพต้นฉบับ AI วิเคราะห์ภาพและสร้าง Prompts เลียนแบบสไตล์",
    descriptionEn: "Generate prompts from original images. AI analyzes image and creates style-mimicking prompts.",
    icon: <Copy className="h-8 w-8" />,
    credits: "4",
    link: "/ai-chat/clone-img",
    color: "from-pink-500 to-rose-500",
    badge: { th: "ใหม่", en: "New" }
  },
  {
    id: "professional",
    titleTh: "Professional Mode",
    titleEn: "Professional Mode",
    descriptionTh: "สร้างวิดีโอระดับมืออาชีพด้วย Template สำเร็จรูปและการตั้งค่าขั้นสูง",
    descriptionEn: "Create professional videos with ready-made templates and advanced settings.",
    icon: <Crown className="h-8 w-8" />,
    credits: "50+",
    link: "/professional",
    color: "from-yellow-500 to-amber-500",
    badge: { th: "VIP", en: "VIP" }
  },
  {
    id: "ai-chat",
    titleTh: "AI Chat",
    titleEn: "AI Chat",
    descriptionTh: "สนทนากับ Gemini AI วิเคราะห์รูปภาพ และ Clone รูป/วิดีโอ",
    descriptionEn: "Chat with Gemini AI, analyze images, and clone images/videos.",
    icon: <MessageSquare className="h-8 w-8" />,
    credits: "ฟรี / Free",
    link: "/ai-chat",
    color: "from-teal-500 to-cyan-500"
  },
  {
    id: "watermark",
    titleTh: "สร้างลายน้ำ",
    titleEn: "Add Watermark",
    descriptionTh: "เพิ่มลายน้ำบนวิดีโอหรือรูปภาพ เลือกตำแหน่งและข้อความได้ตามต้องการ",
    descriptionEn: "Add watermark to videos or images. Customize position and text as needed.",
    icon: <Stamp className="h-8 w-8" />,
    credits: "5",
    link: "/watermark",
    color: "from-slate-500 to-gray-600",
    badge: { th: "ใหม่", en: "New" }
  },
  {
    id: "videobulk",
    titleTh: "สร้างวิดีโอแบบ Bulk",
    titleEn: "Bulk Video Generation",
    descriptionTh: "สร้างวิดีโอหลายชิ้นพร้อมกัน ใส่ Prompt ทีละบรรทัด ระบบจะสร้างให้อัตโนมัติ",
    descriptionEn: "Generate multiple videos at once. Enter prompts line by line for automatic batch processing.",
    icon: <Layers className="h-8 w-8" />,
    credits: "30-60",
    link: "/generate/videobulk",
    color: "from-blue-500 to-indigo-500",
    badge: { th: "Bulk", en: "Bulk" }
  },
  {
    id: "photobulk",
    titleTh: "สร้างรูปภาพแบบ Bulk",
    titleEn: "Bulk Photo Generation",
    descriptionTh: "สร้างรูปภาพหลายชิ้นพร้อมกัน ใส่ Prompt ทีละบรรทัด หรืออัปโหลดไฟล์ .txt/.csv",
    descriptionEn: "Generate multiple photos at once. Enter prompts line by line or upload .txt/.csv file.",
    icon: <Layers className="h-8 w-8" />,
    credits: "4-16",
    link: "/generate/photobulk",
    color: "from-rose-500 to-pink-500",
    badge: { th: "Bulk", en: "Bulk" }
  },
  {
    id: "videomerge",
    titleTh: "รวมวิดีโอ AI",
    titleEn: "Video Merge",
    descriptionTh: "รวมวิดีโอ 2-5 ไฟล์เข้าด้วยกัน เรียงลำดับได้ตามต้องการ พร้อมดาวน์โหลดไฟล์เดียว",
    descriptionEn: "Merge 2-5 video files together. Set your preferred order and download as a single file.",
    icon: <Layers className="h-8 w-8" />,
    credits: "10",
    link: "/generate/videomerge",
    color: "from-orange-500 to-red-500",
    badge: { th: "ใหม่", en: "New" }
  },
  {
    id: "text-to-speech",
    titleTh: "สร้างเสียงพูด AI",
    titleEn: "AI Text to Speech",
    descriptionTh: "สร้างเสียงพูดสมจริงด้วย ElevenLabs รองรับหลายภาษา และปรับแต่งเสียงได้",
    descriptionEn: "Generate realistic speech with ElevenLabs. Multi-language support with customizable voice settings.",
    icon: <Mic className="h-8 w-8" />,
    credits: "14/1K",
    link: "/generate/text-to-speech",
    color: "from-violet-500 to-purple-600",
    badge: { th: "ใหม่", en: "New" }
  },
  {
    id: "text-to-dialogue",
    titleTh: "บทสนทนา 2 คน AI",
    titleEn: "AI Two-Person Dialogue",
    descriptionTh: "สร้างบทสนทนาระหว่าง 2 คน พร้อมอารมณ์และจังหวะการพูดที่เป็นธรรมชาติ",
    descriptionEn: "Create natural conversations between 2 speakers with emotions and realistic timing.",
    icon: <Users className="h-8 w-8" />,
    credits: "14/1K",
    link: "/generate/text-to-dialogue",
    color: "from-pink-500 to-purple-500",
    badge: { th: "ใหม่", en: "New" }
  },
];


const AllServices = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">
              <Zap className="h-3 w-3 mr-1" />
              {t("บริการทั้งหมด", "All Services")}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              <span className="text-gradient">{t("บริการ AI", "AI Services")}</span> {t("ครบวงจร", "All-in-One")}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t(
                "เลือกบริการที่ต้องการ สร้างวิดีโอ รูปภาพ เพลง ด้วย AI ที่ทันสมัยที่สุด",
                "Choose your service. Generate videos, images, and music with the most advanced AI."
              )}
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service) => (
              <Card
                key={service.id}
                variant={service.popular ? "glow" : "glass"}
                className="relative overflow-hidden group hover:scale-[1.02] transition-all duration-300"
              >
                {/* Badge */}
                {service.badge && (
                  <Badge
                    className="absolute top-4 right-4 z-10"
                    variant={service.popular ? "default" : "secondary"}
                  >
                    {t(service.badge.th, service.badge.en)}
                  </Badge>
                )}

                <CardHeader className="pb-2">
                  {/* Icon */}
                  <div
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform duration-300`}
                  >
                    {service.icon}
                  </div>
                  <CardTitle className="text-xl">
                    {t(service.titleTh, service.titleEn)}
                  </CardTitle>
                  <CardDescription className="line-clamp-2">
                    {t(service.descriptionTh, service.descriptionEn)}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pb-4">
                  {/* Credits */}
                  <div className="flex items-center gap-2 text-sm">
                    <Coins className="h-4 w-4 text-primary" />
                    <span className="text-muted-foreground">
                      {service.credits === "ฟรี / Free"
                        ? t("ฟรี", "Free")
                        : `${service.credits} ${t("เครดิต", "credits")}`}
                    </span>
                  </div>
                </CardContent>

                <CardFooter>
                  <Button asChild variant="hero" className="w-full group/btn">
                    <Link to={service.link}>
                      {t("เริ่มใช้งาน", "Get Started")}
                      <ArrowRight className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Quick Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card variant="glass" className="p-6 text-center">
              <div className="text-3xl font-bold text-gradient">10+</div>
              <div className="text-sm text-muted-foreground mt-1">
                {t("บริการ AI", "AI Services")}
              </div>
            </Card>
            <Card variant="glass" className="p-6 text-center">
              <div className="text-3xl font-bold text-gradient">5+</div>
              <div className="text-sm text-muted-foreground mt-1">
                {t("โมเดลวิดีโอ", "Video Models")}
              </div>
            </Card>
            <Card variant="glass" className="p-6 text-center">
              <div className="text-3xl font-bold text-gradient">3+</div>
              <div className="text-sm text-muted-foreground mt-1">
                {t("โมเดลรูปภาพ", "Image Models")}
              </div>
            </Card>
            <Card variant="glass" className="p-6 text-center">
              <div className="text-3xl font-bold text-gradient">24/7</div>
              <div className="text-sm text-muted-foreground mt-1">
                {t("พร้อมใช้งาน", "Available")}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AllServices;
